
import { PartnerSales } from '../page';

interface PartnerDetailProps {
  partner: PartnerSales;
  onBack: () => void;
  onDelete?: () => void;
  onApprove?: (action: 'approve' | 'reject') => void;
  timeFilter: 'week' | 'month' | 'year';
  onTimeFilterChange: (filter: 'week' | 'month' | 'year') => void;
  currentUser: any;
}

export default function PartnerDetail({
  partner,
  onBack,
  onDelete,
  onApprove,
  timeFilter,
  onTimeFilterChange,
  currentUser
}: PartnerDetailProps) {
  const isManager = currentUser?.role === 'manager';

  const getStatusBadge = () => {
    if (partner.status === 'pending') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <i className="ri-time-line mr-2"></i>
          Chờ phê duyệt
        </span>
      );
    } else if (partner.status === 'approved' && partner.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <i className="ri-check-line mr-2"></i>
          Đã phê duyệt
        </span>
      );
    } else if (partner.status === 'approved' && !partner.isActive) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
          <i className="ri-pause-line mr-2"></i>
          Tạm dừng
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
          <i className="ri-close-line mr-2"></i>
          Đã từ chối
        </span>
      );
    }
  };

  const getRevenueByTimeFilter = () => {
    switch (timeFilter) {
      case 'week':
        return partner.thisWeekRevenue;
      case 'month':
        return partner.thisMonthRevenue;
      case 'year':
        return partner.thisYearRevenue;
      default:
        return partner.totalRevenue;
    }
  };

  const getTimeFilterLabel = () => {
    switch (timeFilter) {
      case 'week':
        return 'tuần này';
      case 'month':
        return 'tháng này';
      case 'year':
        return 'năm này';
      default:
        return 'tổng';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <i className="ri-arrow-left-line text-gray-600"></i>
          </button>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
              Chi tiết đối tác
            </h1>
            <p className="text-sm lg:text-base text-gray-600 mt-1">
              Thông tin chi tiết và hiệu suất bán hàng
            </p>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {isManager && partner.status === 'pending' && onApprove && (
            <>
              <button
                onClick={() => onApprove('approve')}
                className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-check-line mr-2"></i>
                Phê duyệt
              </button>
              <button
                onClick={() => onApprove('reject')}
                className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-close-line mr-2"></i>
                Từ chối
              </button>
            </>
          )}
          
          {isManager && onDelete && (
            <button
              onClick={onDelete}
              className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-2"></i>
              Xóa đối tác
            </button>
          )}
        </div>
      </div>

      {/* Partner Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {partner.partnerName.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">{partner.partnerName}</h2>
                <p className="text-gray-600">{partner.email}</p>
                <div className="mt-2">
                  {getStatusBadge()}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Hoa hồng</h3>
                <p className="text-2xl font-bold text-gray-900">{partner.commissionRate}%</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Tổng đơn hàng</h3>
                <p className="text-2xl font-bold text-gray-900">{partner.totalOrders}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Đơn hàng cuối</h3>
                <p className="text-sm text-gray-900">
                  {partner.lastOrderDate 
                    ? new Date(partner.lastOrderDate).toLocaleDateString('vi-VN')
                    : 'Chưa có đơn hàng'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Hiệu suất bán hàng {getTimeFilterLabel()}
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              { key: 'week', label: 'Tuần này' },
              { key: 'month', label: 'Tháng này' },
              { key: 'year', label: 'Năm này' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => onTimeFilterChange(key as any)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  timeFilter === key
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Doanh thu</p>
                <p className="text-2xl font-bold">
                  {getRevenueByTimeFilter().toLocaleString('vi-VN')}đ
                </p>
              </div>
              <i className="ri-money-dollar-circle-line text-3xl text-blue-200"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Hoa hồng nhận</p>
                <p className="text-2xl font-bold">
                  {(getRevenueByTimeFilter() * partner.commissionRate / 100).toLocaleString('vi-VN')}đ
                </p>
              </div>
              <i className="ri-hand-coin-line text-3xl text-green-200"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Tổng hoa hồng</p>
                <p className="text-2xl font-bold">
                  {partner.totalCommission.toLocaleString('vi-VN')}đ
                </p>
              </div>
              <i className="ri-trophy-line text-3xl text-purple-200"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Top Products */}
      {partner.topProducts && partner.topProducts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">
            Sản phẩm bán chạy nhất
          </h3>
          
          <div className="space-y-4">
            {partner.topProducts.map((product, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-semibold text-sm">#{index + 1}</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{product.productName}</h4>
                    <p className="text-sm text-gray-600">Đã bán: {product.quantity} sản phẩm</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">
                    {product.revenue.toLocaleString('vi-VN')}đ
                  </p>
                  <p className="text-sm text-gray-600">
                    Hoa hồng: {(product.revenue * partner.commissionRate / 100).toLocaleString('vi-VN')}đ
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Information */}
      {partner.status === 'pending' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <i className="ri-information-line text-yellow-600 text-xl mt-0.5"></i>
            <div>
              <h4 className="font-medium text-yellow-800">Đối tác đang chờ phê duyệt</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Đối tác này chưa được phê duyệt và chưa thể bắt đầu bán hàng. 
                {isManager && ' Bạn có thể phê duyệt hoặc từ chối đối tác này.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {partner.status === 'rejected' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <i className="ri-close-circle-line text-red-600 text-xl mt-0.5"></i>
            <div>
              <h4 className="font-medium text-red-800">Đối tác đã bị từ chối</h4>
              <p className="text-sm text-red-700 mt-1">
                Đối tác này đã bị từ chối và không thể sử dụng hệ thống.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
